import { useState } from "react";
import {
  ApiRequestError,
  assistMapping,
  getMapping,
  type MappingAssistResult,
  putMapping,
} from "../lib/client";
import {
  MAPPING_TYPE_LABELS,
  MAPPING_TYPES,
  type MappingSource,
  type MappingType,
} from "../lib/types";

type Status =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "loaded" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "conflict"; message: string }
  | { kind: "error"; message: string };

/**
 * Read-modify-write editor for a single mapping_type.
 *
 * PUT replaces the entire named blob, so the flow is always: GET → edit → PUT
 * the whole thing back. We GET on load, let the operator edit raw JSON, validate
 * it parses locally, then PUT. The server still validates structure (400) and
 * nothing is written on a 400.
 *
 * GET returns `{ source, mapping }`: when the subscriber has no custom mapping
 * the server hands back the default (`source: "default"`), which the operator
 * can edit and save to create their own.
 */
export function MappingEditor({ subscriberId }: { subscriberId: string }) {
  const [mappingType, setMappingType] = useState<MappingType>(
    "main_opportunity_mapping",
  );
  const [text, setText] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [source, setSource] = useState<MappingSource | null>(null);
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [parseError, setParseError] = useState<string | null>(null);

  // --- Natural-language assistant state ---
  const [assistText, setAssistText] = useState("");
  const [assisting, setAssisting] = useState(false);
  const [assistError, setAssistError] = useState<string | null>(null);
  const [proposal, setProposal] = useState<MappingAssistResult | null>(null);

  function resetAssist() {
    setAssistError(null);
    setProposal(null);
  }

  async function load() {
    setStatus({ kind: "loading" });
    setParseError(null);
    setDirty(false);
    setLoaded(false);
    setSource(null);
    resetAssist();
    try {
      const { source: src, mapping } = await getMapping(
        subscriberId,
        mappingType,
      );
      setText(JSON.stringify(mapping, null, 2));
      setSource(src);
      setLoaded(true);
      setStatus({ kind: "loaded" });
    } catch (err) {
      setText("");
      if (err instanceof ApiRequestError && err.status === 409) {
        setStatus({ kind: "conflict", message: err.message });
      } else {
        setStatus({
          kind: "error",
          message:
            err instanceof ApiRequestError
              ? err.message
              : "Failed to load mapping.",
        });
      }
    }
  }

  function validateLocal(): unknown | undefined {
    try {
      const parsed = JSON.parse(text);
      setParseError(null);
      return parsed;
    } catch (err) {
      setParseError(err instanceof Error ? err.message : "Invalid JSON.");
      return undefined;
    }
  }

  async function save() {
    const parsed = validateLocal();
    if (parsed === undefined) return;
    setStatus({ kind: "saving" });
    try {
      // PUT returns an empty 200; the text we sent is the saved state.
      await putMapping(subscriberId, mappingType, parsed);
      setText(JSON.stringify(parsed, null, 2));
      setSource("subscriber");
      setDirty(false);
      setStatus({ kind: "saved" });
    } catch (err) {
      setStatus({
        kind: "error",
        message:
          err instanceof ApiRequestError
            ? err.message
            : "Failed to save mapping.",
      });
    }
  }

  function prettify() {
    const parsed = validateLocal();
    if (parsed !== undefined) setText(JSON.stringify(parsed, null, 2));
  }

  async function askClaude() {
    // The agent reasons over what's in the editor right now (edits included),
    // so the current text must be valid JSON before we send it.
    const current = validateLocal();
    if (current === undefined) return;
    setAssisting(true);
    setAssistError(null);
    setProposal(null);
    try {
      const result = await assistMapping(mappingType, current, assistText);
      setProposal(result);
    } catch (err) {
      setAssistError(
        err instanceof ApiRequestError
          ? err.message
          : "Assistant request failed.",
      );
    } finally {
      setAssisting(false);
    }
  }

  function applyProposal() {
    if (!proposal) return;
    setText(JSON.stringify(proposal.mapping, null, 2));
    setDirty(true);
    setProposal(null);
    if (status.kind === "saved") setStatus({ kind: "loaded" });
  }

  const busy = status.kind === "loading" || status.kind === "saving";

  return (
    <section className="panel">
      <div className="toolbar">
        <select
          className="text-input"
          value={mappingType}
          disabled={busy}
          onChange={(e) => {
            setMappingType(e.target.value as MappingType);
            setLoaded(false);
            setDirty(false);
            setText("");
            setSource(null);
            setStatus({ kind: "idle" });
            resetAssist();
          }}
        >
          {MAPPING_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn primary"
          onClick={load}
          disabled={busy}
        >
          {status.kind === "loading" ? "Loading…" : "Load mapping"}
        </button>
        {loaded && (
          <>
            <button
              type="button"
              className="btn"
              onClick={prettify}
              disabled={busy}
            >
              Format
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={save}
              disabled={busy || !dirty}
              title={dirty ? "PUT the whole blob" : "No changes to save"}
            >
              {status.kind === "saving" ? "Saving…" : "Save (PUT)"}
            </button>
            {dirty && <span className="hint">unsaved changes</span>}
          </>
        )}
        <span className="grow" />
        <code className="muted">{MAPPING_TYPE_LABELS[mappingType]}</code>
      </div>

      {loaded && source === "default" && status.kind !== "saved" && (
        <div className="banner warn">
          Showing the <strong>default</strong> <code>{mappingType}</code> — this
          subscriber has no custom mapping yet. Edit and save to create one.
        </div>
      )}
      {status.kind === "saved" && (
        <div className="banner ok">Saved. This affects the next import.</div>
      )}
      {status.kind === "conflict" && (
        <div className="banner error">
          Stored mapping is invalid against the current model (409). Fix and
          re-save. {status.message}
        </div>
      )}
      {status.kind === "error" && (
        <div className="banner error">{status.message}</div>
      )}
      {parseError && (
        <div className="banner error">Invalid JSON: {parseError}</div>
      )}

      {loaded && (
        <div className="assist">
          <label className="assist-label" htmlFor="assist-input">
            ✨ Describe the change in plain English — a Claude agent reads the
            ps-integrations repo and proposes an updated{" "}
            <code>{mappingType}</code>.
          </label>
          <textarea
            id="assist-input"
            className="assist-input"
            placeholder={
              "e.g. Also map the SF custom field Preferred_Name__c to the " +
              "constituent's preferred name, and stop importing the description."
            }
            spellCheck
            value={assistText}
            disabled={assisting}
            onChange={(e) => setAssistText(e.target.value)}
          />
          <div className="assist-actions">
            <button
              type="button"
              className="btn primary"
              onClick={askClaude}
              disabled={assisting || !assistText.trim()}
            >
              {assisting
                ? "Asking Claude… (this can take a minute)"
                : "Ask Claude"}
            </button>
            {assisting && (
              <span className="hint">
                Reading the repo and reasoning about the mapping…
              </span>
            )}
          </div>

          {assistError && <div className="banner error">{assistError}</div>}

          {proposal && (
            <div className="proposal">
              <div className="proposal-summary">
                <strong>Proposed change</strong>
                <p>{proposal.summary}</p>
              </div>
              {proposal.warnings.length > 0 && (
                <ul className="proposal-warnings">
                  {proposal.warnings.map((w) => (
                    <li key={w}>⚠️ {w}</li>
                  ))}
                </ul>
              )}
              <pre className="proposal-json">
                {JSON.stringify(proposal.mapping, null, 2)}
              </pre>
              <div className="assist-actions">
                <button
                  type="button"
                  className="btn primary"
                  onClick={applyProposal}
                >
                  Apply to editor
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setProposal(null)}
                >
                  Discard
                </button>
                <span className="hint">
                  Review, then <strong>Save (PUT)</strong> above to persist.
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {loaded && (
        <textarea
          className="json-editor"
          spellCheck={false}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setDirty(true);
            if (status.kind === "saved") setStatus({ kind: "loaded" });
          }}
        />
      )}

      {loaded && (
        <p className="hint footnote">
          ⚠️ PUT replaces the entire <code>{mappingType}</code> blob — this
          editor holds the full read-modify-write payload. Omitting a field
          drops it.
        </p>
      )}
    </section>
  );
}
